import * as React from 'react';
import { cn } from '@common/bw-ui/lib/utils';
import { Panel } from './panel';

export interface StepperStep {
  label: string;
}

export interface StepperProps {
  steps: StepperStep[];
  activeStep: number;
  className?: string;
}

function StepConnector() {
  return (
    <div
      data-slot="connector"
      className="tw:h-0.5 tw:bg-border tw:mx-1 tw:md:w-12"
    />
  );
}

function StepNumber({ index, active }: { index: number; active: boolean }) {
  return (
    <span
      data-slot="step-number"
      {...(active && { 'data-active': '' })}
      className="tw:flex tw:h-8 tw:w-8 tw:md:h-10 tw:md:w-10 tw:items-center tw:justify-center tw:rounded-full tw:text-sm tw:md:text-base tw:shrink-0 tw:justify-self-center"
    >
      {index + 1}
    </span>
  );
}

function StepLabel({
  label,
  index,
  active,
}: {
  label: string;
  index: number;
  active: boolean;
}) {
  return (
    <span
      data-slot="step-label"
      {...(active && { 'data-active': '' })}
      style={{ '--index': index } as React.CSSProperties}
      className="tw:row-start-2 tw:[grid-column:calc(var(--index)*2+1)] tw:text-xs tw:md:text-sm tw:text-center"
    >
      {label}
    </span>
  );
}

export function Stepper({ steps, activeStep, className }: StepperProps) {
  return (
    <Panel
      data-slot="stepper"
      className={cn(
        'tw:px-6 tw:py-4 tw:md:py-6 tw:flex tw:items-center tw:justify-center',
        className,
      )}
    >
      <div
        data-slot="stepper-grid"
        className={cn(
          // Base grid layout - use CSS var for columns so we can override with variants
          'tw:grid tw:items-center tw:gap-y-1 tw:md:gap-y-2 tw:mx-auto tw:w-fit tw:md:max-w-md',
          'tw:[grid-template-columns:var(--grid-cols)]',

          // Full width on mobile when many steps (connectors hidden) so 1fr columns spread evenly
          'tw:many-steps:w-full tw:many-steps:[grid-template-columns:repeat(var(--step-count),1fr)]',
          'tw:md:many-steps:w-fit tw:md:many-steps:[grid-template-columns:var(--grid-cols)]',

          // Gap when many steps
          'tw:many-steps:gap-x-6 tw:md:many-steps:gap-x-0',

          // --- Connector: hide on mobile when many steps ---
          'tw:many-steps:connector:hidden tw:md:many-steps:connector:block',

          // --- Step number: active vs inactive ---
          'tw:step-number:active:bg-primary tw:step-number:active:text-white',
          'tw:step-number:inactive:bg-secondary tw:step-number:inactive:text-muted',

          // --- Label color: active vs inactive ---
          'tw:step-label:active:text-foreground tw:step-label:inactive:text-muted',

          // --- Label visibility: many steps hides all on mobile ---
          'tw:many-steps:step-label:hidden tw:md:many-steps:step-label:block',
        )}
        style={
          {
            '--step-count': steps.length,
            '--grid-cols': steps
              .map((_, i) => (i === 0 ? '1fr' : 'auto 1fr'))
              .join(' '),
          } as React.CSSProperties
        }
      >
        {steps.map((_, index) => (
          <React.Fragment key={index}>
            {index > 0 && <StepConnector />}
            <StepNumber index={index} active={index <= activeStep} />
          </React.Fragment>
        ))}

        {steps.map((step, index) => (
          <StepLabel
            key={index}
            label={step.label}
            index={index}
            active={index <= activeStep}
          />
        ))}
      </div>
    </Panel>
  );
}
